<template>
    <MkA
        class="mention"
        :context-behavior="{ type: 'userCard', username, host }"
        :class="{ isMe: isCurrentUser({ username, host }) }"
        :to="url"
        @click.stop
    >
        <img class="icon" :src="`/avatar/@${username}@${host}`" alt="" />
        <span class="main">
            <span class="username">@{{ username }}</span>
            <span v-if="resolveHost(host) !== null || showFullAcct" class="host"
                >@{{ toUnicode(host) }}</span
            >
        </span>
    </MkA>
</template>

<script lang="ts" setup>
import { toUnicode } from "punycode";
import { magFormatAcct } from "@/scripts-mag/mag-util";
import { resolveHost } from "@/config";
import { useAccountStore } from "@/model/account";
import { storeToRefs } from "pinia";
import { useSettingsStore } from "@/store/settings-store";
import { refProp } from "@/composables/reactive";
import { computed } from "vue";

const accountStore = useAccountStore();
const { isCurrentUser } = storeToRefs(accountStore);

const settingsStore = useSettingsStore();
const { settings } = storeToRefs(settingsStore);
const showFullAcct = refProp(settings, "showFullAcct");

const props = defineProps<{
    username: string;
    host: string;
}>();

const canonical = computed(
    () =>
        `@${magFormatAcct({
            username: props.username,
            host: resolveHost(props.host),
        })}`
);
const url = computed(() => `/${canonical.value}`);
</script>

<style lang="scss" scoped>
.mention {
    position: relative;
    display: inline-block;
    padding: 2px 8px 2px 2px;
    margin-block: 2px;
    border-radius: 999px;
    max-width: 100%;
    white-space: nowrap;
    text-overflow: ellipsis;
    color: var(--mention);
    isolation: isolate;

    &::before {
        content: "";
        position: absolute;
        inset: 0;
        border-radius: inherit;
        background: color-mix(in srgb, var(--mention) 10%, transparent);
        z-index: -1;
    }

    &.isMe {
        --mention: var(--mentionMe);
    }

    > .icon {
        width: 1.5em;
        height: 1.5em;
        object-fit: cover;
        margin: 0 0.2em 0 0;
        vertical-align: bottom;
        border-radius: 100%;
    }

    > .main > .host {
        opacity: 0.5;
    }
}
</style>
